import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { GLOBE_RADIUS, latLonToVector3, tangentBasis } from "../lib/seismic";
import { useSim } from "../lib/store";

const MAX_BURSTS = 5;
const PER = 1400;
const TOTAL = MAX_BURSTS * PER;
const LIFE = 2.8;

const vertexShader = /* glsl */ `
  uniform float uNow;
  uniform float uLife;
  uniform float uRadius;
  uniform float uStart[${MAX_BURSTS}];
  uniform float uMaxSpread[${MAX_BURSTS}];
  uniform int uCount;
  uniform vec3 uEpi[${MAX_BURSTS}];
  uniform vec3 uT1[${MAX_BURSTS}];
  uniform vec3 uT2[${MAX_BURSTS}];
  attribute float aBurst;
  attribute float aAzimuth;
  attribute float aTarget;
  attribute float aPhase;
  attribute float aSize;
  varying float vFade;
  varying float vDist;
  void main() {
    int bi = int(aBurst + 0.5);
    vec3 epi = vec3(0.0); vec3 t1 = vec3(1.0,0.0,0.0); vec3 t2 = vec3(0.0,0.0,1.0);
    float start = 0.0; float maxS = 0.4; bool active = false;
    for (int i = 0; i < ${MAX_BURSTS}; i++) {
      if (i == bi && i < uCount) {
        epi = uEpi[i]; t1 = uT1[i]; t2 = uT2[i]; start = uStart[i]; maxS = uMaxSpread[i]; active = true;
      }
    }
    float local = (uNow - start);
    float p = local / uLife;
    if (!active || p < 0.0 || p > 1.0) {
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      gl_PointSize = 0.0;
      return;
    }
    float stream = clamp((p - aPhase * 0.4) / (1.0 - aPhase * 0.4 + 0.001), 0.0, 1.0);
    float dist = aTarget * maxS * stream;
    vDist = clamp(stream, 0.0, 1.0);
    vFade = sin(stream * 3.14159) * (1.0 - p * 0.25);
    vec3 dir = cos(aAzimuth) * t1 + sin(aAzimuth) * t2;
    vec3 sp = normalize(epi * cos(dist) + dir * sin(dist));
    vec3 pos = sp * (uRadius + 0.02 * (1.0 - vDist));
    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    gl_PointSize = aSize * vFade * (220.0 / -mv.z);
    gl_Position = projectionMatrix * mv;
  }
`;

const fragmentShader = /* glsl */ `
  uniform vec3 uCore;
  uniform vec3 uEdge;
  varying float vFade;
  varying float vDist;
  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    if (d > 0.5) discard;
    float alpha = smoothstep(0.5, 0.0, d);
    vec3 col = mix(uCore, uEdge, vDist);
    gl_FragColor = vec4(col, alpha * max(vFade, 0.0) * 0.6);
  }
`;

export function AftershockSwarm({ event }) {
  const pointsRef = useRef();
  const aftershocks = event?.aftershocks;

  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(TOTAL * 3);
    const aBurst = new Float32Array(TOTAL);
    const aAzimuth = new Float32Array(TOTAL);
    const aTarget = new Float32Array(TOTAL);
    const aPhase = new Float32Array(TOTAL);
    const aSize = new Float32Array(TOTAL);
    for (let i = 0; i < TOTAL; i++) {
      aBurst[i] = Math.floor(i / PER);
      aAzimuth[i] = Math.random() * Math.PI * 2;
      aTarget[i] = Math.pow(Math.random(), 1.6);
      aPhase[i] = Math.random();
      aSize[i] = 5 + Math.random() * 10;
    }
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("aBurst", new THREE.BufferAttribute(aBurst, 1));
    g.setAttribute("aAzimuth", new THREE.BufferAttribute(aAzimuth, 1));
    g.setAttribute("aTarget", new THREE.BufferAttribute(aTarget, 1));
    g.setAttribute("aPhase", new THREE.BufferAttribute(aPhase, 1));
    g.setAttribute("aSize", new THREE.BufferAttribute(aSize, 1));
    return g;
  }, []);

  const uniforms = useMemo(
    () => ({
      uNow: { value: 0 },
      uLife: { value: LIFE },
      uRadius: { value: GLOBE_RADIUS },
      uCount: { value: 0 },
      uStart: { value: new Array(MAX_BURSTS).fill(0) },
      uMaxSpread: { value: new Array(MAX_BURSTS).fill(0.4) },
      uEpi: { value: Array.from({ length: MAX_BURSTS }, () => new THREE.Vector3(0, 1, 0)) },
      uT1: { value: Array.from({ length: MAX_BURSTS }, () => new THREE.Vector3(1, 0, 0)) },
      uT2: { value: Array.from({ length: MAX_BURSTS }, () => new THREE.Vector3(0, 0, 1)) },
      uCore: { value: new THREE.Color("#ffd9c0") },
      uEdge: { value: new THREE.Color("#c81818") },
    }),
    []
  );

  useEffect(() => {
    if (!event || !aftershocks?.length) {
      uniforms.uCount.value = 0;
      return;
    }
    const count = Math.min(MAX_BURSTS, aftershocks.length);
    uniforms.uCount.value = count;
    for (let i = 0; i < count; i++) {
      const s = aftershocks[i];
      const lat = THREE.MathUtils.clamp(event.lat + s.latOff, -89, 89);
      const lon = event.lon + s.lonOff;
      const epi = latLonToVector3(lat, lon, 1).normalize();
      const { t1, t2 } = tangentBasis(epi);
      uniforms.uEpi.value[i].copy(epi);
      uniforms.uT1.value[i].copy(t1);
      uniforms.uT2.value[i].copy(t2);
      uniforms.uStart.value[i] = s.t;
      uniforms.uMaxSpread.value[i] = THREE.MathUtils.mapLinear(s.mag, 4, 8, 0.14, 0.5);
    }
  }, [aftershocks, event, uniforms]);

  useFrame(() => {
    const st = useSim.getState();
    const pts = pointsRef.current;
    if (!pts) return;
    if (!st.simulating || !aftershocks?.length) {
      pts.visible = false;
      return;
    }
    pts.visible = true;
    uniforms.uNow.value = (performance.now() - st.simStart) / 1000;
  });

  return (
    <points ref={pointsRef} geometry={geometry} frustumCulled={false} visible={false}>
      <shaderMaterial
        uniforms={uniforms}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
